import { createSlice } from '@reduxjs/toolkit';
import { fetchAdvisersThunk } from './thunks/fetch-advisers-thunk';
import { fetchAdviserThunk } from './thunks/fetch-adviser-thunk';
import { approveAdviserThunk } from './thunks/approve-adviser-thunk';
import { revokeAdviserApprovalThunk } from './thunks/revoke-adviser-approval-thunk';
import { updateAdviserContactThunk } from './thunks/update-adviser-contact-thunk';
import { updateAdviserOrganisationThunk } from './thunks/update-adviser-organisation-thunk';
import { updateAdviserLicenseThunk } from './thunks/update-adviser-license-thunk';
import { updateAdviserSettingsThunk } from './thunks/update-adviser-settings-thunk';
import type { AdviserDocument, AdviserMetadata } from '@syfo/types/adviser.types';

export interface AdvisersState {
  list: AdviserMetadata[];
  isLoading: boolean;
  error: string | null;
  lastFetchedAt: string | null;
  
  // Individual adviser state
  selectedAdviser: AdviserDocument | null;
  isLoadingAdviser: boolean;
  adviserError: string | null;
  
  // Update operations state
  isUpdating: boolean;
  updateError: string | null;
  lastUpdatedAt: string | null;
}

const initialState: AdvisersState = {
  list: [],
  isLoading: false,
  error: null,
  lastFetchedAt: null,
  
  selectedAdviser: null,
  isLoadingAdviser: false,
  adviserError: null,
  
  isUpdating: false,
  updateError: null,
  lastUpdatedAt: null
};

const advisersSlice = createSlice({
  name: 'advisers',
  initialState,
  reducers: {
    // Reset advisers list state
    resetAdvisers: (state) => {
      state.list = [];
      state.error = null;
      state.lastFetchedAt = null;
    },
    // Reset selected adviser state
    resetSelectedAdviser: (state) => {
      state.selectedAdviser = null;
      state.adviserError = null;
    },
    // Reset update state
    resetUpdateState: (state) => {
      state.updateError = null;
      state.lastUpdatedAt = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch advisers list
    builder
      .addCase(fetchAdvisersThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAdvisersThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.list = action.payload;
        state.error = null;
        state.lastFetchedAt = new Date().toISOString();
      })
      .addCase(fetchAdvisersThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch advisers';
      });

    // Fetch individual adviser
    builder
      .addCase(fetchAdviserThunk.pending, (state) => {
        state.isLoadingAdviser = true;
        state.adviserError = null;
      })
      .addCase(fetchAdviserThunk.fulfilled, (state, action) => {
        state.isLoadingAdviser = false;
        state.selectedAdviser = action.payload;
        state.adviserError = null;
      })
      .addCase(fetchAdviserThunk.rejected, (state, action) => {
        state.isLoadingAdviser = false;
        state.adviserError = action.error.message || 'Failed to fetch adviser';
      });

    // Update adviser contact
    builder
      .addCase(updateAdviserContactThunk.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
      })
      .addCase(updateAdviserContactThunk.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.updateError = null;
        state.lastUpdatedAt = new Date().toISOString();
        // Update the selected adviser's contact if present
        if (state.selectedAdviser) {
          const updatedContact = action.meta.arg.contact;
          state.selectedAdviser.contact = updatedContact;
        }
      })
      .addCase(updateAdviserContactThunk.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = action.error.message || 'Failed to update contact';
      });

    // Update adviser organisation
    builder
      .addCase(updateAdviserOrganisationThunk.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
      })
      .addCase(updateAdviserOrganisationThunk.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.updateError = null;
        state.lastUpdatedAt = new Date().toISOString();
        // Update the selected adviser's organisation if present
        if (state.selectedAdviser) {
          const updatedOrganisation = action.meta.arg.organisation;
          state.selectedAdviser.organisation = updatedOrganisation;
        }
      })
      .addCase(updateAdviserOrganisationThunk.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = action.error.message || 'Failed to update organisation';
      });

    // Update adviser license
    builder
      .addCase(updateAdviserLicenseThunk.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
      })
      .addCase(updateAdviserLicenseThunk.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.updateError = null;
        state.lastUpdatedAt = new Date().toISOString();
        // Update the selected adviser's license if present
        if (state.selectedAdviser) {
          const updatedLicense = action.meta.arg.license;
          state.selectedAdviser.license = updatedLicense;
        }
      })
      .addCase(updateAdviserLicenseThunk.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = action.error.message || 'Failed to update license';
      });

    // Update adviser settings
    builder
      .addCase(updateAdviserSettingsThunk.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
      })
      .addCase(updateAdviserSettingsThunk.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.updateError = null;
        state.lastUpdatedAt = new Date().toISOString();
        // Update the selected adviser's settings if present
        if (state.selectedAdviser) {
          const updatedSettings = action.meta.arg.settings;
          state.selectedAdviser.settings = updatedSettings;
        }
      })
      .addCase(updateAdviserSettingsThunk.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = action.error.message || 'Failed to update settings';
      });

    // Approve adviser
    builder
      .addCase(approveAdviserThunk.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
      })
      .addCase(approveAdviserThunk.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.updateError = null;
        state.lastUpdatedAt = new Date().toISOString();
        // Update the selected adviser's approval if present
        if (state.selectedAdviser) {
          const approval = action.meta.arg.approval;
          state.selectedAdviser.approval = approval;
        }
      })
      .addCase(approveAdviserThunk.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = action.error.message || 'Failed to approve adviser';
      });

    // Revoke adviser approval
    builder
      .addCase(revokeAdviserApprovalThunk.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
      })
      .addCase(revokeAdviserApprovalThunk.fulfilled, (state) => {
        state.isUpdating = false;
        state.updateError = null;
        state.lastUpdatedAt = new Date().toISOString();
        // Remove the selected adviser's approval if present
        if (state.selectedAdviser) {
          state.selectedAdviser.approval = undefined;
        }
      })
      .addCase(revokeAdviserApprovalThunk.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = action.error.message || 'Failed to revoke adviser approval';
      });
  }
});

export const { resetAdvisers, resetSelectedAdviser, resetUpdateState } = advisersSlice.actions;
export default advisersSlice.reducer;